// frontend/src/components/dashboard/pages/profilesetting/styles/profileStyles.js
import { alpha } from '@mui/material/styles';

export const gradients = {
  primary: 'linear-gradient(145deg, #2A47FF 0%, #5580FF 100%) !important',
  secondary: 'linear-gradient(145deg, #FF6B6B 0%, #FF0055 100%) !important',
  success: 'linear-gradient(145deg, #57f167 0%, #10B981 100%) !important',
  info: 'linear-gradient(145deg, #0EA5E9 0%, #38BDF8 100%) !important',
  warning: 'linear-gradient(145deg, #F59E0B 0%, #FBBF24 100%) !important',
  error: 'linear-gradient(145deg, #EF4444 0%, #DC2626 100%) !important',
  purple: 'linear-gradient(145deg, #8B5CF6 0%, #7C3AED 100%) !important',
  pink: 'linear-gradient(145deg, #EC4899 0%, #F472B6 100%) !important',
  dark: 'linear-gradient(145deg, #1F2937 0%, #111827 100%) !important',
  light: 'linear-gradient(145deg, #F9FAFB 0%, #F3F4F6 100%) !important',
  background: 'linear-gradient(145deg, #f6f9fc 0%, #EEF2FF 100%) !important'
};

// Instagram-inspired color palette
export const instagramColors = {
  blue: '#0095F6',
  red: '#ED4956',
  purple: '#8134AF',
  yellow: '#FCAF45',
  green: '#66C15A',
  darkBlue: '#00376B',
  lightBlue: '#E7F3FF',
  grey: '#8E8E8E',
  lightGrey: '#EFEFEF',
  darkGrey: '#262626',
  white: '#FFFFFF',
  black: '#000000',
};

export const profileStyles = (theme) => ({
  // Common styles
  root: {
    backgroundColor: `${theme.palette.mode === 'dark' ? theme.palette.background.default : '#FAFAFA'} !important`,
    padding: `${theme.spacing(3)} !important`,
    borderRadius: `${theme.shape.borderRadius * 2}px !important`,
    transition: 'all 0.3s ease !important',
    boxShadow: `${theme.shadows[1]} !important`,
    '&:hover': {
      boxShadow: `${theme.shadows[4]} !important`,
    },
  },
  
  // Header styles for profile settings
  header: {
    background: `${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.15) : '#FFFFFF'} !important`,
    padding: `${theme.spacing(3)} !important`,
    borderRadius: `${theme.shape.borderRadius * 2}px !important`,
    marginBottom: `${theme.spacing(3)} !important`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.05) !important',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'space-between !important',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column !important',
      alignItems: 'stretch !important',
      gap: `${theme.spacing(2)} !important`,
    },
  },

  title: {
    fontWeight: '700 !important',
    color: `${theme.palette.mode === 'dark' ? theme.palette.common.white : instagramColors.darkGrey} !important`,
    fontSize: '1.75rem !important',
  },

  subtitle: {
    color: `${theme.palette.mode === 'dark' ? theme.palette.grey[400] : instagramColors.grey} !important`,
    marginTop: `${theme.spacing(1)} !important`,
    marginBottom: '0 !important',
    fontSize: '0.95rem !important',
  },

  // Section styles
  section: {
    background: `${theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : '#FFFFFF'} !important`,
    padding: `${theme.spacing(3)} !important`,
    borderRadius: `${theme.shape.borderRadius * 2}px !important`,
    marginBottom: `${theme.spacing(3)} !important`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.05) !important',
    transition: 'all 0.3s ease !important',
    overflow: 'hidden !important',
    border: `${theme.palette.mode === 'dark' ? 'none' : '1px solid rgba(0,0,0,0.05)'} !important`,
    '&:hover': {
      boxShadow: '0 8px 24px rgba(0,0,0,0.08) !important',
    },
  },

  sectionTitle: {
    position: 'relative !important',
    fontWeight: '600 !important',
    marginBottom: `${theme.spacing(3)} !important`,
    color: `${theme.palette.mode === 'dark' ? theme.palette.common.white : instagramColors.darkGrey} !important`,
    display: 'inline-block !important',
    '&:after': {
      content: '""',
      position: 'absolute !important',
      left: '0 !important',
      bottom: '-8px !important',
      width: '40px !important',
      height: '3px !important',
      borderRadius: '1.5px !important',
      background: `${instagramColors.blue} !important`,
    },
  },

  // Avatar styles
  avatar: {
    width: '120px !important',
    height: '120px !important',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15) !important',
    border: `4px solid #FFFFFF !important`,
  },

  avatarWrapper: {
    position: 'relative !important',
    marginBottom: `${theme.spacing(3)} !important`,
    display: 'inline-block !important',
  },

  avatarButtons: {
    position: 'absolute !important',
    bottom: '5px !important',
    right: '5px !important',
    display: 'flex !important',
    gap: `${theme.spacing(1)} !important`,
  },

  // Form elements
  formLabel: {
    fontWeight: '600 !important',
    marginBottom: `${theme.spacing(1)} !important`,
    color: `${theme.palette.mode === 'dark' ? theme.palette.grey[300] : instagramColors.darkGrey} !important`,
    fontSize: '0.875rem !important',
  },

  formField: {
    marginBottom: `${theme.spacing(3)} !important`,
  },

  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: `${theme.shape.borderRadius}px !important`,
      transition: 'all 0.2s ease !important',
      borderColor: `${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.2) : instagramColors.lightGrey} !important`,
      '&:hover': {
        borderColor: `${instagramColors.blue} !important`,
      },
      '&.Mui-focused': {
        boxShadow: `0 0 0 2px ${alpha(instagramColors.blue, 0.25)} !important`,
      },
    },
    '& .MuiInputLabel-root': {
      color: `${instagramColors.grey} !important`,
    },
  },

  // Social media styles
  socialIcon: {
    color: `${theme.palette.mode === 'dark' ? theme.palette.grey[400] : instagramColors.grey} !important`,
    backgroundColor: `${theme.palette.mode === 'dark' 
      ? alpha(theme.palette.common.white, 0.1) 
      : alpha(theme.palette.common.black, 0.05)} !important`,
    borderRadius: '50% !important',
    padding: `${theme.spacing(1)} !important`,
    marginRight: `${theme.spacing(1)} !important`,
    transition: 'all 0.3s ease !important',
    '&:hover': {
      backgroundColor: `${theme.palette.mode === 'dark' 
        ? alpha(theme.palette.common.white, 0.15) 
        : alpha(theme.palette.common.black, 0.08)} !important`,
      transform: 'translateY(-2px) !important',
    },
  },

  socialField: {
    display: 'flex !important',
    alignItems: 'center !important',
  },

  // Button styles
  submitButton: {
    background: `${instagramColors.blue} !important`,
    color: `${theme.palette.common.white} !important`,
    padding: `${theme.spacing(1.5)} ${theme.spacing(3)} !important`,
    fontWeight: '600 !important',
    boxShadow: `0 4px 14px ${alpha(instagramColors.blue, 0.4)} !important`,
    borderRadius: `${theme.shape.borderRadius * 1.5}px !important`,
    textTransform: 'none !important',
    '&:hover': {
      background: `${alpha(instagramColors.blue, 0.9)} !important`,
      boxShadow: `0 6px 20px ${alpha(instagramColors.blue, 0.6)} !important`,
      transform: 'translateY(-2px) !important',
    },
    transition: 'all 0.2s ease !important',
  },

  cancelButton: {
    color: `${instagramColors.grey} !important`,
    fontWeight: '600 !important',
    textTransform: 'none !important',
    '&:hover': {
      backgroundColor: `${alpha(theme.palette.common.black, 0.04)} !important`,
    },
  },

  actionButton: {
    borderRadius: '8px !important',
    padding: `${theme.spacing(1)} ${theme.spacing(2)} !important`,
    fontWeight: '600 !important',
    textTransform: 'none !important',
    transition: 'all 0.2s ease !important',
    '&:hover': {
      transform: 'translateY(-2px) !important',
      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)} !important`,
    },
  },

  // Instagram profile card styles
  profileCard: {
    borderRadius: `${theme.breakpoints.down('sm') ? 0 : theme.shape.borderRadius * 3}px !important`,
    overflow: 'hidden !important',
    boxShadow: '0 16px 40px rgba(0,0,0,0.12) !important',
    background: `${theme.palette.background.paper} !important`,
    maxWidth: '100% !important',
    width: '100% !important',
    maxHeight: '90vh !important',
    overflowY: 'auto !important',
    '&::-webkit-scrollbar': {
      width: '4px !important',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: `${alpha(theme.palette.common.black, 0.03)} !important`,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: `${alpha(theme.palette.common.black, 0.2)} !important`,
      borderRadius: '2px !important',
      '&:hover': {
        backgroundColor: `${alpha(theme.palette.common.black, 0.3)} !important`,
      },
    },
  },
  
  profileCardCover: {
    height: '240px !important',
    background: `${gradients.primary} !important`,
    position: 'relative !important',
  },
  
  profileCardCoverGradient: gradients.primary,
  
  profileCardAvatar: {
    width: '120px !important',
    height: '120px !important',
    border: `4px solid ${theme.palette.background.paper} !important`,
    boxShadow: '0 8px 20px rgba(0,0,0,0.2) !important',
  },
  
  // Instagram stats badges
  statsBadge: {
    display: 'flex !important',
    flexDirection: 'column !important', 
    alignItems: 'center !important',
    padding: `${theme.spacing(1)} !important`,
    '& .MuiTypography-h6': {
      fontWeight: '700 !important',
      fontSize: '1.1rem !important',
    },
    '& .MuiTypography-body2': {
      color: `${instagramColors.grey} !important`,
      fontSize: '0.8rem !important',
    }
  },

  // Achievement badges
  achievementBadge: {
    width: '65px !important',
    height: '65px !important',
    borderRadius: '50% !important',
    display: 'flex !important',
    justifyContent: 'center !important',
    alignItems: 'center !important',
    backgroundColor: `${alpha(instagramColors.blue, 0.2)} !important`,
    color: `${instagramColors.blue} !important`,
    border: `2px solid ${instagramColors.blue} !important`,
    margin: '0 8px 8px 8px !important',
  },

  // Instagram stories-like highlights 
  storyHighlight: {
    width: '70px !important',
    height: '70px !important', 
    borderRadius: '50% !important',
    display: 'flex !important',
    justifyContent: 'center !important',
    alignItems: 'center !important',
    margin: '0 8px !important',
    border: '2px solid transparent !important',
    background: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}) padding-box, 
                linear-gradient(45deg, #FCAF45, #E1306C, #5851DB) border-box !important`,
    '& .MuiTypography-caption': {
      marginTop: `${theme.spacing(1)} !important`,
      color: `${instagramColors.grey} !important`,
      fontSize: '0.7rem !important',
    }
  },

  // Instagram profile header
  instagramHeader: {
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'space-between !important',
    padding: `${theme.spacing(2)} !important`,
    borderBottom: `1px solid ${theme.palette.divider} !important`,
  },

  // Instagram profile name and info
  instagramUsername: {
    fontWeight: '600 !important',
    fontSize: '0.9rem !important',
    display: 'flex !important',
    alignItems: 'center !important',
  },

  instagramBio: {
    margin: `${theme.spacing(2)} 0 !important`,
    whiteSpace: 'pre-line !important',
    lineHeight: '1.7 !important',
    fontSize: '0.9rem !important',
  },

  instagramStat: {
    textAlign: 'center !important',
    '& .MuiTypography-h6': {
      fontWeight: '700 !important',
      fontSize: '1.1rem !important',
    },
    '& .MuiTypography-body2': {
      color: `${instagramColors.grey} !important`,
      fontSize: '0.75rem !important',
    }
  },

  // Instagram post-like card
  instagramPost: {
    borderRadius: '0 !important',
    marginBottom: `${theme.spacing(2)} !important`,
    border: `${theme.palette.mode === 'dark' ? 'none' : `1px solid ${instagramColors.lightGrey}`} !important`,
  },

  instagramPostHeader: {
    padding: `${theme.spacing(2)} !important`,
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'space-between !important',
  },

  instagramPostActions: {
    padding: `${theme.spacing(1, 2)} !important`,
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'space-between !important',
  },

  instagramPostCaption: {
    padding: `${theme.spacing(0, 2, 2, 2)} !important`,
    '& .MuiTypography-body2': {
      fontSize: '0.9rem !important',
    }
  },

  // Notification styles
  successNotification: {
    backgroundColor: `${instagramColors.green} !important`,
    color: `${theme.palette.common.white} !important`,
    borderRadius: `${theme.shape.borderRadius * 2}px !important`,
    boxShadow: `${theme.shadows[3]} !important`,
  },

  errorNotification: {
    backgroundColor: `${instagramColors.red} !important`,
    color: `${theme.palette.common.white} !important`,
    borderRadius: `${theme.shape.borderRadius * 2}px !important`,
    boxShadow: `${theme.shadows[3]} !important`,
  },

  // Badges
  verifiedBadge: {
    color: `${instagramColors.blue} !important`,
    marginLeft: `${theme.spacing(0.5)} !important`,
  },

  // Loading states
  loadingButton: {
    position: 'relative !important',
    '& .MuiCircularProgress-root': {
      position: 'absolute !important',
      top: '50% !important',
      left: '50% !important',
      marginTop: '-12px !important',
      marginLeft: '-12px !important',
    },
  },

  // Social media buttons
  socialMediaButton: {
    backgroundColor: `${theme.palette.background.paper} !important`,
    color: `${instagramColors.blue} !important`,
    boxShadow: '0px 3px 10px rgba(0,0,0,0.1) !important',
    padding: `${theme.spacing(1.5)} !important`,
    transition: 'all 0.3s ease !important',
    '&:hover': {
      transform: 'translateY(-3px) !important',
      boxShadow: '0px 6px 15px rgba(0,0,0,0.15) !important',
    },
  },

  // Quote styles
  quote: {
    fontStyle: 'italic !important',
    color: `${instagramColors.darkGrey} !important`,
    textAlign: 'center !important',
    padding: `${theme.spacing(3)} !important`,
    position: 'relative !important',
    '&:before': {
      content: '"\\201C"',
      position: 'absolute !important',
      top: '0 !important',
      left: '8px !important',
      fontSize: '4rem !important',
      color: `${alpha(instagramColors.blue, 0.2)} !important`,
      fontFamily: 'Georgia, serif !important',
      lineHeight: '1 !important',
    },
    '&:after': {
      content: '"\\201D"',
      position: 'absolute !important',
      bottom: '-20px !important',
      right: '8px !important',
      fontSize: '4rem !important',
      color: `${alpha(instagramColors.blue, 0.2)} !important`,
      fontFamily: 'Georgia, serif !important',
      lineHeight: '1 !important',
    }
  },
});